/**
 * Backfill wizard timeline preferences — the roll/ripple default, the drag snap
 * granularity, and whether the timeline is shown at all.
 *
 * Account-scoped when a PreferencesProvider is mounted. Unlike the older
 * preference hooks there is deliberately **no `kalpa-*` localStorage key**: this
 * namespace shipped server-backed from day one, so there is no legacy per-browser
 * value to migrate and nothing for a fallback key to rescue. Provider-free mounts
 * (component tests) therefore keep the value in memory for the session, which is
 * exactly what those mounts need.
 *
 * The persisted `edit_mode` is the *default* for a drag. The wizard layers a
 * per-gesture override on top of it — the mode belongs to the gesture, not to a
 * block, so nothing here is ever stored per segment.
 */
import { useCallback, useMemo, useState } from 'react';

import {
  BackfillTimelineSettings,
  DEFAULT_BACKFILL_TIMELINE,
  loadBackfillTimelineSettings,
  TimelineEditMode,
  usePreferences,
} from '../contexts/PreferencesContext';

export interface BackfillTimelinePreferences {
  settings: BackfillTimelineSettings;
  setEditMode: (mode: TimelineEditMode) => void;
  setSnapMinutes: (minutes: number) => void;
  setShowTimeline: (visible: boolean) => void;
  resetToDefault: () => void;
}

export const useBackfillTimelinePreferences = (): BackfillTimelinePreferences => {
  const store = usePreferences();
  const [local, setLocal] = useState<BackfillTimelineSettings>(() => ({
    ...DEFAULT_BACKFILL_TIMELINE,
  }));

  // Re-merge the server value over the code defaults on every read, so a new
  // field ships correct without anyone having written it.
  const settings = useMemo(() => {
    if (!store) return local;
    return loadBackfillTimelineSettings(
      store.settings.backfill_timeline as Record<string, unknown>,
    );
  }, [store, local]);

  const write = useCallback(
    (next: BackfillTimelineSettings) => {
      setLocal(next);
      if (store) {
        store.setNamespace('backfill_timeline', next as unknown as Record<string, unknown>);
      }
    },
    [store],
  );

  const setEditMode = useCallback(
    (mode: TimelineEditMode) => write({ ...settings, edit_mode: mode }),
    [settings, write],
  );

  const setSnapMinutes = useCallback(
    (value: number) => {
      const clamped = Math.min(60, Math.max(1, Math.floor(value)));
      write({ ...settings, snap_minutes: clamped });
    },
    [settings, write],
  );

  const setShowTimeline = useCallback(
    (visible: boolean) => write({ ...settings, show_timeline: visible }),
    [settings, write],
  );

  const resetToDefault = useCallback(() => {
    setLocal({ ...DEFAULT_BACKFILL_TIMELINE });
    if (store) store.resetNamespace('backfill_timeline');
  }, [store]);

  return { settings, setEditMode, setSnapMinutes, setShowTimeline, resetToDefault };
};

export default useBackfillTimelinePreferences;
